import { ChangeDetectionStrategy, Component, inject, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  IAlertComponent,
  IButtonDirective,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountSelectionService } from "libs/m19-services/src/lib/m19-services/accountSelection.service";
import { AsinService } from "libs/m19-services/src/lib/m19-services/asin.service";
import { switchMap } from "rxjs";

@Component({
  selector: "app-custom-field-edition",
  template: ` <div class="flex flex-col items-start justify-between gap-4" *transloco="let t">
    <IAlert type="info" [title]="t('custom-field-settings.custom_field_tuto')" />
    <div class="w-full">
      <IFormField>
        <label iLabel>{{ t("custom-field-edition.custom_field_1") }}</label>
        <input
          iInput
          [(ngModel)]="productTag1Name"
          placeholder="{{ t('custom-field-settings.enter_a_name_for_this_custom_field') }}"
          maxlength="128"
        />
      </IFormField>
    </div>
    <div class="w-full">
      <IFormField>
        <label iLabel>{{ t("custom-field-edition.custom_field_2") }}</label>
        <input
          iInput
          [(ngModel)]="productTag2Name"
          placeholder="{{ t('custom-field-settings.enter_a_name_for_this_custom_field') }}"
          maxlength="128"
        />
      </IFormField>
    </div>
    <div class="mt-4 flex w-full justify-between">
      <button iButton variant="secondary" size="sm" (click)="modalRef.close()">
        {{ t("common.cancel") }}
      </button>
      <button iButton size="sm" [loading]="loading()" (click)="updateCustomFieldName()">
        {{ t("common.update") }}
      </button>
    </div>
  </div>`,

  imports: [
    TranslocoDirective,
    IButtonDirective,
    FormsModule,
    IFormFieldComponent,
    ILabelComponent,
    IInputDirective,
    IAlertComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldSettingsComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly asinService = inject(AsinService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toastrService = inject(ToasterService);

  modalRef = inject(ModalRef);

  public readonly inventoryConfig = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) =>
        this.asinService.getInventoryConfig(accountMarketplace.accountId, accountMarketplace.marketplace),
      ),
    ),
  );
  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  productTag1Name = linkedSignal(
    () =>
      this.inventoryConfig()?.customField1Name ??
      this.translocoService.translate("custom-field-edition.custom_field_1"),
  );
  productTag2Name = linkedSignal(
    () =>
      this.inventoryConfig()?.customField2Name ??
      this.translocoService.translate("custom-field-edition.custom_field_2"),
  );

  loading = signal(false);

  updateCustomFieldName() {
    this.loading.set(true);
    if (!this.productTag1Name() || this.productTag1Name().trim().length === 0) {
      this.productTag1Name.set(this.translocoService.translate("custom-field-edition.custom_field_1"));
    }
    if (!this.productTag2Name() || this.productTag2Name().trim().length === 0) {
      this.productTag2Name.set(this.translocoService.translate("custom-field-edition.custom_field_2"));
    }

    this.asinService
      .setCustomFieldNames(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.productTag1Name() ?? "",
        this.productTag2Name() ?? "",
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("catalog-page.custom_fields_settings_updated"));
          this.modalRef.close();
          this.loading.set(false);
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("catalog-page.failed_to_update_custom_fields_settings"),
          );
          this.loading.set(false);
        },
      });
  }
}
